import { Icon } from '@iconify/react';
import {
  Button,
  Card,
  CardFooter,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Textarea,
} from '@nextui-org/react';
import { Handle, Position } from '@xyflow/react';
import React, { useEffect, useState } from 'react';

interface AINodeProps {
  data: {
    title: string;
    agentId: string;
    description: string;
    systemPrompt?: string;
    modelOptions: { key: string; value: string }[];
    temperature?: number;
    inputs: {
      id: string;
      label: string;
      style?: React.CSSProperties;
      isReadOnly?: boolean;
      defaultValue?: string;
    }[];
    outputs: {
      id: string;
      style?: React.CSSProperties;
    }[];
    style: {
      iconColor: string;
      handleColor: string;
    };
    showTemperature?: boolean;
    showApiKey?: boolean;
  };
}

const AINodeInput: React.FC<{
  id: string;
  label: string;
  style?: React.CSSProperties;
  isReadOnly?: boolean;
  defaultValue?: string;
  handleColor: string;
}> = ({ id, label, style, isReadOnly, defaultValue, handleColor }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [inputValue, setInputValue] = useState(defaultValue);
  const [tempValue, setTempValue] = useState(inputValue);

  useEffect(() => {
    setInputValue(defaultValue);
  }, [defaultValue]);

  return (
    <>
      <div key={id} className="space-y-2 relative px-4">
        <span className="text-sm font-semibold">{label}</span>
        <Input
          size="lg"
          placeholder="Receiving input"
          labelPlacement="outside"
          isReadOnly={isReadOnly}
          value={inputValue}
          className="max-w-full"
          endContent={
            <Icon
              icon="charm:screen-maximise"
              className="text-2xl text-default-400 cursor-pointer"
              onClick={() => {
                console.log('working...');
                setTempValue(inputValue);
                setIsModalOpen(true);
              }}
            />
          }
        />
        <Handle
          key={id}
          type={'target'}
          position={Position.Left}
          id={id}
          style={style}
          className={`!w-3 !h-3 !top-1/2 absolute border-none bg-${handleColor}-500`}
        />
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        size="5xl"
      >
        <ModalContent>
          <ModalHeader>
            <h3 className="text-lg font-semibold">Edit {label}</h3>
          </ModalHeader>
          <ModalBody>
            <Textarea
              value={tempValue}
              onChange={(e) => setTempValue(e.target.value)}
              placeholder={`Enter ${label.toLowerCase()}...`}
              minRows={20}
            />
          </ModalBody>
          <ModalFooter>
            <Button
              color="danger"
              variant="light"
              onClick={() => setIsModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={() => {
                setInputValue(tempValue);
                setIsModalOpen(false);
              }}
            >
              Save Changes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

const AINode: React.FC<AINodeProps> = ({ data }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(data?.title);
  const [temperature, setTemperature] = useState(data?.temperature || 0.7);
  const { handleColor, iconColor } = data?.style || {};

  useEffect(() => {
    if (data) {
      setTitle(data.title);
      setTemperature(data.temperature || 0.7);
    }
  }, [data]);

  console.log(data);

  if (!data) return null;
  return (
    <Card className="w-[400px] hover:border-1 hover:border-black">
      {/* Render input handles */}
      <div className="space-y-4 py-4">
        <div className="flex items-center gap-2 px-4">
          <div className={`w-8 h-8 bg-${iconColor}-500 rounded-lg`} />
          {isEditing ? (
            <Input
              value={title}
              className="max-w-[300px]"
              variant="bordered"
              size="sm"
              autoFocus
              onBlur={() => setIsEditing(false)}
              onValueChange={(value) => setTitle(value)}
              classNames={{
                input: 'font-medium',
                inputWrapper: 'min-h-0 h-auto py-0',
              }}
            />
          ) : (
            <span
              className="font-medium cursor-pointer hover:bg-default-100 px-2 py-1 rounded"
              onClick={() => setIsEditing(true)}
            >
              {title}
            </span>
          )}
        </div>

        <div className="text-sm text-gray-500 px-4">{data.description}</div>

        {data.inputs.map((inputProps) => (
          <AINodeInput
            key={inputProps.id}
            {...inputProps}
            handleColor={handleColor}
          />
        ))}

        {data.modelOptions.length > 0 && (
          <div className="space-y-2 px-4">
            <span className="text-sm font-semibold">Model Name</span>
            <Select
              defaultSelectedKeys={[data.modelOptions[0].key]}
              className="max-w-full"
            >
              {data.modelOptions.map(({ key, value }) => (
                <SelectItem key={key} value={key}>
                  {value}
                </SelectItem>
              ))}
            </Select>
          </div>
        )}

        {data.showApiKey && (
          <div className="space-y-2 px-4">
            <span className="text-sm font-semibold">API Key</span>
            <Input placeholder="Type something..." className="max-w-full" />
          </div>
        )}

        {data.showTemperature && (
          <div className="space-y-2 px-4">
            <span className="text-sm font-semibold">Temperature</span>
            <Input
              type="number"
              value={temperature}
              onChange={(e) => setTemperature(Number(e.target.value))}
              placeholder="0.7"
              min={0}
              max={1}
              step={0.1}
              className="max-w-full"
            />
          </div>
        )}
      </div>

      {/* Render output handles */}
      {data.outputs.map(({ id, style }) => (
        <Handle
          key={id}
          type="source"
          position={Position.Right}
          id={id}
          style={style}
        />
      ))}
      <CardFooter className="bg-default-100 relative">
        <div className="flex justify-end w-full">
          <div className="text-small ">Response</div>
        </div>
        <Handle
          key={`${data.agentId}-response`}
          type={'source'}
          position={Position.Right}
          id={`${data.agentId}-response`}
          className={`!w-3 !h-3 !top-1/2 absolute right-0  border-none bg-${handleColor}-500 `}
        />
      </CardFooter>
    </Card>
  );
};

export default AINode;
